import Content from '~/components/elements/Content'
import { LosseLink, LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import type { LoaderData } from '@ubo/losse-sjedel'
import { useState } from 'react'
import type { Page_Flexcontent_Flex_Banner, Post } from '~/graphql/types'
import Crystal from '~/components/svg/Crystal'
import Breadcrumbs from '~/components/elements/Breadcrumbs'
import ChevronDouble from '~/components/svg/ChevronDouble'
import Download from '~/components/svg/Download'
import ContentstableGrid from '~/components/elements/ContentstableGrid'
import Modal from '~/components/elements/Modal'
import Form from '~/components/elements/Form'
import Loading from '~/components/elements/Loading'
import clsx from 'clsx'

export default function BannerDefault({ fields }: { fields: Page_Flexcontent_Flex_Banner }) {
  // @ts-ignore
  const { hasContentstable } = useLoaderData()
  const { page } = useLoaderData<LoaderData<Post>>()
  const [openDownloadModal, setOpenDownloadModal] = useState(false)
  const isCases = page.databaseId === 1634
  const isDownloadModalLink = fields?.link?.url === '#download'
  const downloadForm = fields?.form || null

  return (
    <section id="BannerDefault" className="relative lg:h-[90vh] xl:max-h-[750px] lg:mb-[21rem]">
      <div className="relative max-w-[2200px] h-full mx-auto">
        <div className="relative overflow-hidden h-full w-full">
          <div className="absolute right-0 bottom-36">
            <Crystal type="white" className="w-[264px] h-[308px]" />
          </div>
          <div className="absolute right-6 -bottom-80">
            <Crystal type="green" className="" />
          </div>
        </div>

        <LossePlaatjie
          loading="eager"
          className="relative lg:absolute top-0 left-0 -z-10 h-full w-full object-cover object-top"
          src={fields.background}
          maxwidth={1999}
        />
      </div>

      <div className="container-right">
        <div className="relative lg:absolute lg:-bottom-[17rem] -mt-7 lg:-mt-0">
          <ContentstableGrid>
            <div
              className={clsx(
                isCases ? 'p-6 md:p-10 lg:px-20 lg:pt-[52px] lg:pb-[72px]' : 'p-6 md:p-10 lg:px-16 lg:pt-20 lg:pb-10',
                fields?.bgcolor === 'hariyama' ? 'bg-metric-hariyama' : 'bg-metric-qwilfish',
                'relative xl:min-w-[880px] text-white',
                hasContentstable ? 'lg:w-[90%]' : 'lg:w-3/4'
              )}
              id="BannerDefault-block"
            >
              {fields?.image && (
                <LossePlaatjie
                  src={fields?.image}
                  className="lg:absolute -right-20 lg:-top-20 rounded-2xl object-cover w-auto h-auto max-h-[225px] xl:max-h-[295px] md:aspect-square"
                />
              )}
              {fields?.icon && <LossePlaatjie src={fields.icon} className="absolute right-6 top-6 z-[10] opacity-10" />}
              <Breadcrumbs />
              <Content
                className={clsx(
                  isCases
                    ? 'children-h2:lg:text-5xl children-h2:sm:text-4xl children-h2:text-3xl children-h2:font-bold children-h2:text-white text-lg space-y-4 lg:my-12 my-6 children-p:leading-[35px] lg:children-p:xl:pr-[290px]'
                    : 'children-h2:text-lg children-h2:font-bold children-h2:text-metric-electrike parsecontent-md lg:mt-6 lg:children-p:pr-16',
                  'text-light break-words'
                )}
              >
                {fields?.description}
              </Content>
              <div className="relative flex flex-col md:flex-row items-center mt-5 z-10 w-full md:w-auto">
                {fields.link?.title && (
                  <div className={isCases ? 'flex items-center' : 'w-full md:w-auto'}>
                    {!isDownloadModalLink && (
                      <LosseLink
                        className="btn btn-metric-electrike inline-flex items-center md:mr-5 mb-4 lg:mb-0 w-full md:w-auto min-w-fit"
                        to={fields?.link?.url || '/'}
                        target={fields?.link?.target || '_self'}
                      >
                        {fields.link.title.toLowerCase().includes('download') && <Download className="mr-3" />}
                        <span>{fields.link.title}</span>
                      </LosseLink>
                    )}
                    {isDownloadModalLink && (
                      <LosseLink
                        onClick={() => setOpenDownloadModal(true)}
                        className="btn btn-metric-electrike inline-flex items-center md:mr-5 mb-4 lg:mb-0 w-full md:w-auto min-w-fit"
                      >
                        {fields.link.title.toLowerCase().includes('download') && <Download className="mr-3" />}
                        <span>{fields.link.title}</span>
                      </LosseLink>
                    )}
                    {isCases && <div className="max-lg:hidden italic">en ontdek wat wij voor jouw organisatie kunnen doen</div>}
                  </div>
                )}
                {fields.landingCta?.title && (
                  <LosseLink className="btn btn-white w-full md:w-auto" to={fields.landingCta?.url || '/'}>
                    {fields.landingCta?.title}
                  </LosseLink>
                )}
              </div>
              {fields.scrollto && (
                <div
                  className="hidden relative z-[10] md:flex justify-end animate-bounce"
                  role="button"
                  onClick={() => document.getElementById(fields.scrollto || '')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <ChevronDouble className="rotate-90" />
                </div>
              )}
              {isDownloadModalLink && downloadForm && (
                <Modal isOpen={openDownloadModal} closeFunction={() => setOpenDownloadModal(false)}>
                  <button
                    type="button"
                    onClick={() => setOpenDownloadModal(false)}
                    aria-label="Close modal"
                    className="absolute right-4 top-4 text-white text-2xl leading-none hover:opacity-80"
                  >
                    x
                  </button>
                  <h2 className="text-white text-2xl font-bold mb-8">Download factsheet</h2>
                  <Form
                    data={downloadForm}
                    renderLoading={() => <Loading />}
                    renderStatus={({ content }) => <Content className="text-white">{content}</Content>}
                    className="relative"
                  />
                </Modal>
              )}
            </div>
          </ContentstableGrid>
        </div>
      </div>
    </section>
  )
}
